// AI Assistant: a chat that answers questions about the current industry's
// data only. Every answer lists the figures it used, with their source, so a
// reader can check the claim. The conversation lives in component state and
// is never written to storage; switching industry is a full page load to
// another subdomain, so it starts fresh there.
import { useLayoutEffect, useRef, useState } from "react";
import {
  Badge,
  ChatComposer,
  ChatMessage,
  ChatMessageList,
  ChatPanel,
  CitedFigures,
  EmptyState,
  Notice,
  PageHeader,
  SuggestionChips,
  ThinkingState,
} from "@dashboard-intelligent/ui";
import type { AssistantStatus } from "@dashboard-intelligent/api/src/modules/assistant/types";
import { ApiError, endpoints } from "../endpoints";
import { useIndustry } from "../industry";
import { useApi, type ApiState } from "../useApi";
import { blocksOf, historyFrom, metaFor, waitText, type Entry } from "./assistant/conversation";
import "./assistant/assistant.css";

export function AssistantPage() {
  const { slug, summary } = useIndustry();
  const industryName = summary?.shortName ?? null;
  const status = useApi(() => endpoints.assistantStatus(slug));

  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [followUps, setFollowUps] = useState<string[]>([]);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const busyRef = useRef(false);
  const seq = useRef(0);
  const nextKey = () => `e${++seq.current}`;

  async function ask(text: string) {
    const question = text.trim();
    if (!question || busyRef.current) return;
    busyRef.current = true;

    const history = historyFrom(entriesRef.current);
    const key = nextKey();
    const previousFollowUps = followUps;
    setEntries((prev) => [
      ...prev,
      {
        kind: "message",
        key,
        answered: false,
        message: { id: key, role: "user", content: question, at: new Date().toISOString(), figures: [] },
      },
    ]);
    setDraft("");
    setFollowUps([]);
    setBusy(true);

    try {
      const res = await endpoints.chat(slug, { message: question, history });
      setEntries((prev) => [
        ...prev.map((e) => (e.key === key && e.kind === "message" ? { ...e, answered: true } : e)),
        { kind: "message", key: nextKey(), answered: true, message: res.message },
      ]);
      setFollowUps(res.suggestions);
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      if (error?.status === 0) {
        // Never reached the server: take the question back out of the log and
        // put it in the composer, so one tap resends it.
        setEntries((prev) => [
          ...prev.filter((e) => e.key !== key),
          {
            kind: "notice",
            key: nextKey(),
            tone: "danger",
            title: "Pertanyaan belum terkirim",
            body: `${error.message} Pertanyaan Anda masih ada di kolom tulis.`,
          },
        ]);
        setDraft((current) => (current.trim() ? current : question));
      } else if (error?.status === 429) {
        setEntries((prev) => [
          ...prev,
          {
            kind: "notice",
            key: nextKey(),
            tone: "warning",
            title: "Terlalu banyak pertanyaan",
            body: `Batas pertanyaan untuk sementara tercapai. Coba lagi dalam ${waitText(error.retryAfter)}.`,
          },
        ]);
      } else {
        setEntries((prev) => [
          ...prev,
          {
            kind: "notice",
            key: nextKey(),
            tone: "danger",
            title: "Jawaban gagal dimuat",
            body: err instanceof Error ? err.message : "Terjadi kesalahan. Coba lagi.",
          },
        ]);
      }
      setFollowUps(previousFollowUps);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  const empty = entries.length === 0;
  const starters = status.status === "ready" ? status.data.starters : [];
  const scope = industryName ? `data ${industryName}` : "data industri ini";
  // Chips live in the log, not above the composer (JEV ui.component_recipe
  // chips_placement=log_inline): wrapped two-line chips above the field left
  // the log no height at 375px. They scroll with the conversation instead.
  const pick = (text: string) => void ask(text);

  // Kit gap: ChatMessageList follows the end from its first render, so on a
  // phone an empty conversation opened scrolled past its own intro. Hold the
  // log at the top while it is empty, then hand back to the kit's follow
  // behaviour by jumping to the end when the first question goes out.
  const wasEmpty = useRef(true);
  useLayoutEffect(() => {
    const log = document.querySelector<HTMLElement>(".con-page[data-fill] .con-chat-log");
    if (log) {
      if (empty) log.scrollTop = 0;
      else if (wasEmpty.current) log.scrollTop = log.scrollHeight;
    }
    wasEmpty.current = empty;
  }, [empty, starters.length]);

  return (
    <>
      <PageHeader
        title="AI Assistant"
        description={
          <>
            <span className="assistant-scope">Asisten membaca {scope} saja.</span>
            <ModeLine status={status} />
          </>
        }
      />
      <ChatPanel label={`Asisten AI ${industryName ?? ""}`.trim()}>
        <ChatMessageList label="Percakapan">
          {empty ? (
            <EmptyState
              icon="chat"
              title={`Tanyakan tentang ${scope}`}
              action={
                starters.length > 0 ? (
                  <SuggestionChips label="Pertanyaan awal" suggestions={starters} onPick={pick} disabled={busy} />
                ) : undefined
              }
            >
              {starters.length > 0 ? "Pilih pertanyaan awal atau tulis sendiri." : "Tulis pertanyaan di kolom bawah."} Setiap jawaban
              menyertakan angka yang dirujuk beserta sumbernya.
            </EmptyState>
          ) : null}
          {entries.map((entry) =>
            entry.kind === "notice" ? (
              <Notice key={entry.key} tone={entry.tone} title={entry.title}>
                <p>{entry.body}</p>
              </Notice>
            ) : (
              <ChatMessage
                key={entry.key}
                role={entry.message.role}
                speaker={entry.message.role === "user" ? "Anda" : "Asisten"}
                meta={metaFor(entry.message)}
                citations={
                  entry.message.role === "assistant" && entry.message.figures.length > 0 ? (
                    <CitedFigures
                      items={entry.message.figures.map((f) => ({ label: f.label, value: f.value, source: f.source }))}
                    />
                  ) : undefined
                }
              >
                <MessageBody content={entry.message.content} />
              </ChatMessage>
            ),
          )}
          {busy ? <ThinkingState label={`Membaca ${scope}`} /> : null}
          {!empty && !busy && followUps.length > 0 ? (
            <SuggestionChips label="Pertanyaan lanjutan" suggestions={followUps} onPick={pick} />
          ) : null}
        </ChatMessageList>
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={pick}
          busy={busy}
          label={`Tanyakan sesuatu tentang ${scope}`}
          placeholder="Tulis pertanyaan"
          sendLabel="Kirim pertanyaan"
        />
      </ChatPanel>
    </>
  );
}

/** Which mode answers come from, said plainly: the model, or the offline fallback. */
function ModeLine({ status }: { status: ApiState<AssistantStatus> & { reload: () => void } }) {
  if (status.status === "loading") {
    return <span className="assistant-mode">Memeriksa mode asisten.</span>;
  }
  if (status.status === "error") {
    return (
      <span className="assistant-mode" data-layout="row">
        <span>Mode asisten belum diketahui. {status.error}</span>
        <button type="button" className="btn-secondary" onClick={status.reload}>
          Periksa lagi
        </button>
      </span>
    );
  }
  return status.data.model ? (
    <span className="assistant-mode">
      <Badge tone="neutral" icon="ai">
        Model {status.data.modelName}
      </Badge>{" "}
      Jawaban disusun model dari ringkasan data.
    </span>
  ) : (
    <span className="assistant-mode">
      <Badge tone="info" icon="info">
        Mode offline
      </Badge>{" "}
      Jawaban dihitung langsung dari data, tanpa model AI.
    </span>
  );
}

function MessageBody({ content }: { content: string }) {
  return (
    <>
      {blocksOf(content).map((block, i) =>
        block.kind === "ul" ? (
          <ul key={i}>
            {block.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>
            {block.lines.map((line, j) => (
              <span key={j}>
                {j > 0 ? <br /> : null}
                {line}
              </span>
            ))}
          </p>
        ),
      )}
    </>
  );
}
